import { useEffect, useState, useRef } from "react";
import type { ExperimentKeyValue, ExperimentResult } from "@rflocal/shared/experiments";
import { getExperiment } from "../../../backendapi/experimentApi";
import { useExperimentContext } from "./ExperimentContext";

const DEFAULT_RESULT = (key: ExperimentKeyValue): ExperimentResult => ({
    key,
    enabled: false,
    variant: undefined
});

/** In-memory cache so re-renders / re-mounts don't re-fetch. */
const cache = new Map<string, ExperimentResult>();

function buildCacheKey(
    key: ExperimentKeyValue,
    distinctId: string,
    groups?: Record<string, string>
): string {
    if (!groups || Object.keys(groups).length === 0) {
        return `${key}:${distinctId}`;
    }

    const serializedGroups = Object.entries(groups)
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([groupType, groupKey]) => `${groupType}=${groupKey}`)
        .join(",");

    return `${key}:${distinctId}:${serializedGroups}`;
}

/**
 * Evaluate an experiment via the backend.
 * Delegates the API call to `backendapi/experimentApi.ts` and caches the
 * result in memory for the lifetime of the page.
 *
 * The distinctId and groups are provided by the ExperimentProvider at each app's root:
 * - Core app: reads user.uid + currentWorkspaceId from globalStore
 * - Rapid: reads user.uid or guestUid + currentWorkspace.id from rapid's auth/store
 *
 * This approach is adblocker-proof because the evaluation happens server-side
 * through posthog-node, not through the posthog-js client.
 *
 * @example
 * const { enabled, variant } = useExperiment(ExperimentKey.NewFeature);
 * if (enabled) { ... }
 */
export function useExperiment(key: ExperimentKeyValue): ExperimentResult {
    const { distinctId, groups } = useExperimentContext();
    const cacheKey = buildCacheKey(key, distinctId, groups);
    const [result, setResult] = useState<ExperimentResult>(
        () => cache.get(cacheKey) ?? DEFAULT_RESULT(key)
    );
    const fetchedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!distinctId) {
            setResult(DEFAULT_RESULT(key));
            return;
        }

        setResult(cache.get(cacheKey) ?? DEFAULT_RESULT(key));
    }, [key, distinctId, cacheKey]);

    useEffect(() => {
        if (!distinctId) {
            fetchedRef.current = null;
            return;
        }

        if (fetchedRef.current === cacheKey) return;

        fetchedRef.current = cacheKey;

        const cached = cache.get(cacheKey);
        if (cached) {
            setResult(cached);
            return;
        }

        const fetchExperiment = async () => {
            try {
                const data = await getExperiment(key, { distinctId, groups });
                cache.set(cacheKey, data);
                setResult(data);
            } catch {
                // Silently fall back to disabled on network errors
                setResult(DEFAULT_RESULT(key));
            }
        };

        fetchExperiment();
    }, [key, distinctId, groups, cacheKey]);

    return result;
}
